import { Alert } from '@startuptools/ui/Alert';
import { Card } from '@startuptools/ui/Card';
import { DateFormat } from '@startuptools/ui/DateFormat';
import { Stack } from '@startuptools/ui/Stack';
import { Table } from '@startuptools/ui/Table';
import { Typography } from '@startuptools/ui/Typography';
import { type GqlMeetingConcernsShowQuery } from './MeetingConcernsShow.graphql';
import { Trans, useLingui } from '@lingui/react';
import { GqlAuthKind, GqlWarrantProgramKind } from '../../../graphql/base-types.graphql';
import { McApprovalStatus } from '../../../components/mc-approval-status/McApprovalStatus';
import { MeetingConcernsTableWp } from './MeetingConcernsTableWp';
import { Link } from 'react-router-dom';
import { styled } from '@mui/joy/styles';
import { useNavigate } from '../../../components/Navigate';

const Tr = styled(Link)({ display: 'table-row' });

export const MeetingConcernsTable = ({
  meetingConcerns,
}: {
  meetingConcerns: GqlMeetingConcernsShowQuery['meetingConcerns'];
}) => {
  const { i18n } = useLingui();
  const { link, navigate } = useNavigate();

  if (meetingConcerns.length === 0) {
    return (
      <Card>
        <Alert variant="outlined" color="neutral">
          <Trans id="The meeting has no share issues, options program, authorizations or articles of associations connected" />
        </Alert>
      </Card>
    );
  }

  const aoaMc = meetingConcerns.find(mc => Boolean(mc.articlesOfAssoc));
  const siMcs = meetingConcerns.filter(mc => Boolean(mc.shareIssue));
  const optionsPrograms = meetingConcerns.filter(mc => Boolean(mc.warrantProgram));
  const wpMcs = optionsPrograms.filter(op => op.warrantProgram!.warrantKind === GqlWarrantProgramKind.Warrant);
  const qesoMcs = optionsPrograms.filter(op => op.warrantProgram!.warrantKind === GqlWarrantProgramKind.Employee);
  const niceMcs = optionsPrograms.filter(op => op.warrantProgram!.warrantKind === GqlWarrantProgramKind.Nice);
  const wiseMcs = optionsPrograms.filter(op => op.warrantProgram!.warrantKind === GqlWarrantProgramKind.Wise);
  const agMcs = meetingConcerns.filter(mc => Boolean(mc.authorizationGroup));

  return (
    <Stack gap={4} margin={2}>
      {aoaMc && (
        <Stack gap={2}>
          <Typography level="h4">
            <Trans id="Articles Of Associations" />
          </Typography>
          <Card>
            <Table hoverRow>
              <thead>
                <tr>
                  <th style={{ width: 160 }}>
                    <Trans id="Appendix Number" />
                  </th>
                  <th style={{ width: 160 }}>
                    <Trans id="Decision Status" />
                  </th>
                  <th>
                    <Trans id="Created" />
                  </th>
                </tr>
              </thead>
              <tbody>
                <Tr
                  style={{ cursor: 'pointer' }}
                  to={link(`/board-portal/articles-of-assocs/${aoaMc.articlesOfAssoc!.id}`)}
                >
                  <td>{aoaMc.appendixNumber}</td>
                  <td>
                    <McApprovalStatus>{aoaMc.approved}</McApprovalStatus>
                  </td>
                  <td>
                    <DateFormat locale={i18n.locale}>{aoaMc.articlesOfAssoc!.createdAt}</DateFormat>
                  </td>
                </Tr>
              </tbody>
            </Table>
          </Card>
        </Stack>
      )}

      {wpMcs.length > 0 && (
        <Stack gap={2}>
          <Typography level="h4">
            <Trans id="Warrant Programs" />
          </Typography>
          <MeetingConcernsTableWp mcs={wpMcs} navigate={navigate} />
        </Stack>
      )}

      {qesoMcs.length > 0 && (
        <Stack gap={2}>
          <Typography level="h4">
            <Trans id="Employee Options" />
          </Typography>
          <MeetingConcernsTableWp mcs={qesoMcs} navigate={navigate} />
        </Stack>
      )}

      {wiseMcs.length > 0 && (
        <Stack gap={2}>
          <Typography level="h4">
            <Trans id="WISE" />
          </Typography>
          <MeetingConcernsTableWp mcs={wiseMcs} navigate={navigate} />
        </Stack>
      )}

      {niceMcs.length > 0 && (
        <Stack gap={2}>
          <Typography level="h4">
            <Trans id="NICE" />
          </Typography>
          <MeetingConcernsTableWp mcs={niceMcs} navigate={navigate} />
        </Stack>
      )}

      {siMcs.length > 0 && (
        <Stack gap={2}>
          <Typography level="h4">
            <Trans id="Share Issues" />
          </Typography>
          <Card>
            <Table hoverRow>
              <thead>
                <tr>
                  <th style={{ width: 160 }}>
                    <Trans id="Appendix Number" />
                  </th>
                  <th style={{ width: 160 }}>
                    <Trans id="Decision Status" />
                  </th>
                  <th>
                    <Trans id="Description" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {siMcs.map(mc => (
                  <Tr
                    key={mc.id}
                    style={{ cursor: 'pointer' }}
                    to={link(`/investments/share-issues/${mc.shareIssue!.id}`)}
                  >
                    <td>{mc.appendixNumber}</td>
                    <td>
                      <McApprovalStatus>{mc.approved}</McApprovalStatus>
                    </td>
                    <td>{mc.shareIssue!.description}</td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Stack>
      )}

      {agMcs.length > 0 && (
        <Stack gap={2}>
          <Typography level="h4">
            <Trans id="Authorizations" />
          </Typography>
          <Card>
            <Table hoverRow>
              <thead>
                <tr>
                  <th style={{ width: 160 }}>
                    <Trans id="Appendix Number" />
                  </th>
                  <th style={{ width: 160 }}>
                    <Trans id="Decision Status" />
                  </th>
                  <th>
                    <Trans id="Authorization Regarding" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {agMcs.map(mc => (
                  <Tr
                    key={mc.id}
                    style={{ cursor: 'pointer' }}
                    to={link(`/board-portal/authorization-groups/${mc.authorizationGroup!.id}`)}
                  >
                    <td>{mc.appendixNumber}</td>
                    <td>
                      <McApprovalStatus>{mc.approved}</McApprovalStatus>
                    </td>
                    <td>{mc.authorizationGroup?.authorizations.map(auth => GqlAuthKind[auth.kind]).join(', ')}</td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Stack>
      )}
    </Stack>
  );
};
